import React, { Suspense } from 'react';
import {
  WebinarPlayerContext,
  WebinarPlayerUIState,
  withWebinarPlayerContext,
} from '$/components/WebinarPlayer/context';
import { ClassNames, css } from '@emotion/react';

import {
  PresenterFragment,
  SocialLink,
  RoomType,
  ExitRoomDisplayType,
} from '$/GraphQL/schema/schema';
import formatSeconds from '$/utils/formatSeconds';
import tw from 'twin.macro';
import { LinkedInIcon, TwitterIcon, FacebookIcon, InstagramIcon } from '$/images/Icons';

import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { Trans } from '@lingui/macro';
import PageBuilder from '$/components/ui/Input/PageBuilder';
import { PageMode } from '$/components/ui/Input/PageBuilder.types';
import ApolloForm from '../../ApolloForm';
import { InRoomButton } from '$/components/ui/Button/InRoomButton';
import stripTypenames from '$/utils/stripTypenames';
import { Maybe } from 'graphql/jsutils/Maybe';
import { LogoAndTitleRoomArea } from './RoomPartsBase';
import { useTransform } from 'framer-motion';
import { withContextValue } from '$/hooks/withPartialContexts';
import { usePropertyFilledLink } from '$/components/ui/EditableInput/DangerouslySetInnerHTML';
import CirclePicture from '$/components/ui/CirclePicture';

const FormatPhone = React.lazy(() => import('$/utils/phone'));
const Carousel = React.lazy(() =>
  import('react-responsive-carousel').then(({ Carousel }) => ({ default: Carousel }))
);
const SocialIcon: React.FC<{ type: string }> = ({ type }) => {
  switch (type) {
    case 'instagram':
      return <InstagramIcon className='h-1_5e' />;
    case 'linkedin':
      return <LinkedInIcon className='h-1_5e' />;
    case 'twitter':
      return <TwitterIcon className='h-1_5e' />;
    default:
      return <FacebookIcon className='h-1_5e' />;
  }
};

export const SocialLinks: React.FC<{ links: SocialLink }> = ({ links }) => {
  links = stripTypenames(links);

  return (
    <>
      {Object.entries(links).map(([key, value]: [string, Maybe<string>]) => {
        if (value) {
          return (
            <a className='social-icon' key={key} href={value} target='_blank'>
              <SocialIcon type={key} />
            </a>
          );
        }

        return null;
      })}
    </>
  );
};

const ExitRoomCTA = withWebinarPlayerContext(
  ['ewebinar', 'set', 'attendee', 'locale'],
  'ExitRoomCTA'
)(({ context }) => {
  const { ewebinar, set } = context;

  const ctaLink = usePropertyFilledLink(ewebinar.exitRoomSettings.exitRoomCTA?.link);

  const onClick = React.useCallback(() => {
    if (!ctaLink) return;
    window.open(ctaLink, '_blank');
  }, [ctaLink]);

  if (
    ewebinar.exitRoomSettings.exitRoomDisplayType !== ExitRoomDisplayType.Cta ||
    !ewebinar.exitRoomSettings.exitRoomCTA
  )
    return null;

  return (
    <ClassNames>
      {({ css, cx }): JSX.Element => (
        <PageBuilder.Button
          onClick={onClick}
          name='exitRoomSettings.exitRoomCTA.name'
          className='mt-3e order-last mx-auto flex leading-normal'
          state={{ mode: PageMode.public, ewebinar, set }}
          as={InRoomButton}
          inputProps={{
            className: cx(
              '',
              css`
                min-height: 1.8rem !important;
              `
            ),
          }}
          placeholder='Name this button'
          linkToolbarDisabled
        />
      )}
    </ClassNames>
  );
});

type ExitRoomBody = Pick<WebinarPlayerUIState, 'ewebinar'> & { location: string };
const ExitRoomBody = withContextValue<WebinarPlayerUIState, ExitRoomBody, ExitRoomBody>(
  WebinarPlayerContext,
  ({ dimensions, ewebinar }) => ({ location: dimensions.actionsPane.location, ewebinar }),
  'ExitRoomBody'
)(({ ewebinar }) => {
  const renderPresenters = React.useCallback((presenters: PresenterFragment[]): JSX.Element[] => {
    return presenters.map((presenter) => {
      const phone = presenter.phone;

      return (
        <div
          key={presenter.id}
          className='presenter justify-center'
          css={css`
            a {
              color: #000;
            }
            a:hover {
              text-decoration: underline;
            }
            .info-line {
              ${tw`text-md leading-normal mb-2 break-all`};
            }
            .social-icon {
              margin-right: 0.5rem;
              &:last-child {
                margin-right: 0;
              }
            }
          `}
        >
          <CirclePicture className='avatar' pictureRef={presenter.profileMediaRef} size={56} />
          <div className='text-lg leading-7'>{presenter.name}</div>
          {presenter.title && <div className='text-md leading-5'>{presenter.title}</div>}
          {presenter.company && <div className='text-md leading-5'>{presenter.company}</div>}
          {presenter.displayEmail && (
            <div className='info-line mb-0 leading-8'>
              <a className='' href={`mailto:${presenter.displayEmail}`}>
                {presenter.displayEmail}
              </a>
            </div>
          )}
          {phone && (
            <div className='info-line'>
              <Suspense>
                <FormatPhone asLink phone={phone} />
              </Suspense>
            </div>
          )}
          {presenter.socialLinks && (
            <div className='flex items-center justify-center mt-2'>
              <SocialLinks links={presenter.socialLinks} />
            </div>
          )}
        </div>
      );
    });
  }, []);

  const useCarousel = false; // DD: We have enough room to show both.  Will need this with more presenters location === 'bottom';

  return React.useMemo(
    () => (
      <ApolloForm className='flex w-full h-full justify-center' defaultValues={ewebinar}>
        <div
          className={`${useCarousel && 'useCarousel'}`}
          css={css`
            .presenters {
              ${tw`overflow-x-scroll md:overflow-x-auto flex items-start justify-center`}

              .presenter {
                ${tw`relative flex flex-col items-center`}
                margin: 1rem;
                padding: 0 2rem 1rem;
                min-width: 16rem;
                max-width: 20rem;
                .content {
                  ${tw`bg-white rounded-lg mt-6 ml-1_5e`}
                  padding: 1rem 1rem 1rem 3rem;
                  overflow: hidden;
                  & div {
                    word-break: break-all;
                    display: -webkit-box;
                    -webkit-line-clamp: 1;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                  }
                }
                .avatar {
                  ${tw`rounded-full mb-1/2e bg-center bg-cover bg-white border-2 border-white`}
                }
              }
              .carousel {
                .slider-wrapper {
                  .slide {
                    background: none !important;
                    .presenter {
                      margin: auto;
                      .avatar {
                        top: -2rem;
                        left: 50%;
                        margin-left: -2rem;
                      }
                      .content {
                        margin-left: 0;
                        margin-top: 2rem;
                        padding: 2.5rem 1rem 1.5rem 1rem;
                        > div {
                          justify-content: center;
                        }
                      }
                    }
                  }
                }
                .control-dots {
                  margin: 0;
                  .dot {
                    opacity: 0.4;
                    background: #ffffff;
                    box-shadow: none;
                    margin: 0 0.375rem;
                    width: 0.7rem;
                    height: 0.7rem;
                    outline: none;
                    &.selected {
                      opacity: 1;
                    }
                  }
                }
              }
            }
          `}
        >
          <div className='flex flex-col text-2xl text-center items-center mb-2e'>
            <Trans>This session has ended. Thanks for watching!</Trans>
          </div>

          {ewebinar.presenters &&
            ewebinar.exitRoomSettings.exitRoomDisplayType === ExitRoomDisplayType.Presenters &&
            !useCarousel && (
              <div className='flex presenters mt-2e'>{renderPresenters(ewebinar.presenters)}</div>
            )}
          <ExitRoomCTA />
          {ewebinar.presenters && useCarousel && (
            <div
              className='presenters'
              css={css`
                .dot {
                  background: #0c0c0c !important;
                  &.selected {
                    background: #000 !important;
                  }
                }
              `}
            >
              <React.Suspense>
                <Carousel
                  autoPlay
                  swipeable
                  showIndicators={ewebinar.presenters.length > 1}
                  showArrows={true}
                  showThumbs={false}
                  showStatus={false}
                  infiniteLoop={true}
                  transitionTime={1500}
                  interval={5000}
                >
                  {renderPresenters(ewebinar.presenters)}
                </Carousel>
              </React.Suspense>
            </div>
          )}
        </div>
      </ApolloForm>
    ),
    [ewebinar, renderPresenters, useCarousel]
  );
});

const ExitRoomFooter = withWebinarPlayerContext(
  ['playbackPositionValue', 'rooms'],
  'ExitRoomFooter'
)(({ context }) => {
  const { playbackPositionValue, rooms } = context;
  const refTimeDisplay = React.useRef<HTMLDivElement>(null);

  const setTimerText = React.useCallback(
    (pos: number) => {
      const node = refTimeDisplay.current;

      if (!node) {
        return;
      }

      const exitRoom = rooms.getRoom(RoomType.Exit);
      const exitRoomCountdown = exitRoom.duration - (pos - exitRoom.appearAt);
      node.innerText = formatSeconds(exitRoomCountdown, true);
    },
    [rooms]
  );

  React.useLayoutEffect(() => {
    setTimerText(playbackPositionValue.get());
    const unsubscribe = playbackPositionValue.onChange(setTimerText);
    return unsubscribe;
  }, [playbackPositionValue, setTimerText]);
  useTransform(playbackPositionValue, setTimerText);

  return (
    <div
      className='flex justify-center items-center text-lg py-1e md:py-0 mt-6 md:mt-0'
      css={css`
        flex-direction: column;
      `}
    >
      <div className='flex justify-center items-center'>
        <Trans>Webinar room closes in</Trans>
        <span ref={refTimeDisplay} className='ml-1e'></span>
      </div>
    </div>
  );
});

export class ExitRoomParts extends LogoAndTitleRoomArea {
  public bodyComponent(): React.FC | null {
    return ExitRoomBody;
  }

  public footerComponent(): React.FC | null {
    return ExitRoomFooter;
  }
}
